import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { translations } from "../translations"
import Fuse from "fuse.js"
import { app, translate } from "../app"
import { useState } from "react"
import { Footer } from "./footer"
import { languages } from "../locale"
import { Pagination } from "./pagination"
import { Search } from "./searchinput"
import { Tabs } from "./tabs"
import { getLocaleByZendesk } from "../helpers"

export function localizeNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { useGrouping: false }).format(value)
}

export function SearchResults() {
  const [searchParams] = useSearchParams()
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10
  const navigate = useNavigate()
  const { locale: localeParam } = useParams()
  const languageSelected =
    (localeParam ? getLocaleByZendesk(localeParam.toLowerCase()) : undefined) ??
    localStorage.getItem("preferredLanguage") ??
    app.locale
  app.locale = languageSelected
  const locale = languages[languageSelected]?.zendesk as string ?? languageSelected
  const isRTL = languages[languageSelected]?.rtl

  const paramValue = searchParams.get('query') || ''
  const articles: ZendeskArticle[] = Object.values(app.data.zendesk.articles)
  const services = Object.values(app.data.services).filter((x) => x.status !== "archived")

  const isZendeskArticle = (object: any): object is ZendeskArticle => 'section' in object || 'category' in object

  const flattenZendeskData = (data: ZendeskArticle[]) => {
    return data.flatMap(item => {
      const nameEntries = Object.entries(item.name).map(([lang, text]) => ({
        id: item.id,
        section: item.section,
        category: item.category,
        lang,
        type: 'name',
        text,
      }))

      const descriptionEntries = Object.entries(item.description).map(([lang, text]) => ({
        id: item.id,
        section: item.section,
        category: item.category,
        lang,
        type: 'description',
        text,
      }))

      return [...nameEntries, ...descriptionEntries]
    })
  }

  const flattenServiceData = (data: Service[]) => {
    return data.flatMap(item => {
      const nameEntries = item?.name ? Object.entries(item?.name).map(([lang, text]) => ({
        id: item.id,
        status: item.status,
        lang,
        type: 'name',
        text,
      })) : []

      const descriptionEntries = item?.description ? Object.entries(item.description).map(([lang, text]) => ({
        id: item.id,
        status: item.status,
        lang,
        type: 'description',
        text,
      })) : []

      const otherProperties = {
        date_created: item.date_created,
        hasLocation: item.hasLocation,
        address: item.address,
        location: item.location,
        provider: item.provider,
        region: item.region,
        city: item.city,
        contactName: item.contactName,
        contactLastName: item.contactLastName,
        contactTitle: item.contactTitle,
        contactEmail: item.contactEmail,
        contactPhone: item.contactPhone,
        contactInfo: item.contactInfo,
        addHours: item.addHours,
        categories: item.categories,
        subcategories: item.subcategories,
        Accessibility: item.Accessibility,
        Populations: item.Populations,
      }

      const combinedEntries = [...nameEntries, ...descriptionEntries].map(entry => ({
        ...entry,
        ...otherProperties,
      }))

      return combinedEntries
    })
  }

  const options = {
    includeMatches: true,
    keys: ['text', 'address', 'city', 'contactInfo.contact_details', 'description', 'name', 'region', 'id'],
    shouldSort: true,
    customSortFn: (a, b) => {
      return a.score - b.score
    }
  }

  const articleSearch = new Fuse(flattenZendeskData(articles), options)
  const articleSearchResult = articleSearch.search(paramValue || '').map(result => result.item)
  const uniqueArticlesIds = [...new Set(articleSearchResult.map(result => result.id))]
  const aggregatedArticlesResults = uniqueArticlesIds.map(id => articles.find(item => item.id === id))
  const serviceSearch = new Fuse(flattenServiceData(services), options)
  const serviceSearchResult = serviceSearch.search(paramValue || '').map(result => result.item)
  const uniqueServicesIds = [...new Set(serviceSearchResult.map(result => result.id))]
  const aggregatedServicesResults = uniqueServicesIds.map(id => services.find(item => item.id === id))

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleTabChange = () => {
    setCurrentPage(1)
  }

  const formatContent = (description: string) => {
    const decodeHtmlEntities = (str: string) => {
      const element = document.createElement('div')
      if (str) {
        element.innerHTML = str
        return element.textContent || element.innerText || ""
      }
      return ""
    }
    const textOnly = description.replace(/<\/?[^>]+(>|$)/g, "")
    const decodedText = decodeHtmlEntities(textOnly)
    const truncatedText = decodedText.slice(0, 200)
    return decodedText.length > 200 ? `${truncatedText}...` : decodedText
  }

  const formatResultsText = (count: number, query: string) => {
    const template = translate(translations.resultsFor)
    return template
      .replace('{count}', localizeNumber(count, locale))
      .replace('{query}', query)
  }
  
  const getTabItem = (hits: ZendeskArticle[] | Service[]) => {
    const startIdx = (currentPage - 1) * pageSize
    const endIdx = currentPage * pageSize
    const paginatedHits = hits.slice(startIdx, endIdx)

    return (
      <>
      <div className='my-3 text-sm' role="status" aria-live="polite">
        {hits.length > 0 ? (
          <div className="text-gray-500">
            {formatResultsText(hits.length, paramValue)}
          </div>
        ) : (
          <div className="text-gray-500">
            {translate(translations.noResultsToDisplay)}
          </div>
        )}
      </div>
        <div>
          {paginatedHits.map((hit: ZendeskArticle | Service) => (
            <div key={`${hit.id}-${hit.name}`}>
              <a href={`${isZendeskArticle(hit) ? `/${locale.toLowerCase()}/articles/${hit.id}` : `/${locale.toLowerCase()}/services/${hit.id}`}`}>
                <h4 className="text-black">{translate(hit.name)}</h4>
                <div className="text-gray-400 text-sm">{formatContent(translate(hit.description))}</div>
              </a>
              <hr className="divider" />
            </div>
          ))}
        </div>
        <div className="my-16 text-center">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={hits.length}
            onChange={handlePageChange}
          />
        </div>
      </>
    )
  }


  const items = [
    {
      key: '1',
      label: translate(translations.allSearchResults),
      children: getTabItem([...aggregatedArticlesResults, ...aggregatedServicesResults].filter(x => translate(x.name))),
    },
    {
      key: '2',
      label: translate(translations.informationResults),
      children: getTabItem(aggregatedArticlesResults.filter(x => translate(x.name))),
    },
    {
      key: '3',
      label: translate(translations.serviceResults),
      children: getTabItem(aggregatedServicesResults.filter(x => translate(x.name))),
    },
  ]

  const handleSearch = (value: string) => {
    setCurrentPage(1)
    navigate(`/${locale.toLowerCase()}/search-results?query=${value}`)
  }

  return (
    <div className={`overflow-y-auto ${isRTL ? 'rtl' : ''}`}>
      <div className="w-full flex flex-wrap justify-center text-black bg-white">
        <div className="w-full px-4 md:w-2/3 py-16">
          <div>
            <Search blueButton onSearch={handleSearch} defaultValue={paramValue} />
          </div>
          <div className={`mt-8 tabs-container ${isRTL ? 'rtl' : ''}`}>
            <Tabs defaultActiveKey="1" items={items} onChange={handleTabChange} className={`${isRTL ? 'text-right' : 'text-left'}`} style={{ direction: isRTL ? "rtl" : "ltr" }} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
